/**
 * Who this browser is.
 *
 * A player is a UUID and nothing more. It is generated the first time somebody
 * opens the page, kept in `localStorage`, and presented on every join from then
 * on — which is the whole of "log out, come back, and your character is still
 * yours" until M7-01 lands real accounts.
 *
 * The order of the argument matters, because it is easy to get backwards: this
 * is not a stand-in for authentication that we will later replace. It is the
 * *player*, which is a different thing from an account and outlives whether
 * there is one. An account is a credential somebody may attach later so their
 * character survives a cleared cache and follows them to another machine; the
 * player is what the credential would be attached *to*. Building it this way
 * round is what lets a demo be played before it is signed up for.
 *
 * Two honest limitations, neither of which a comment can fix and both of which
 * an account later does: clearing site data loses the character, and anybody
 * holding the id is that player. The id is unguessable and travels over TLS,
 * which is the right amount of care for a playtest and the wrong amount for a
 * world with anything at stake in it.
 */

/** Where it lives. Namespaced, because a browser holds more than one game. */
export const PLAYER_ID_KEY = 'annwn.playerId';

/**
 * Where a signed-in access token lives (M7-01), written by the portal (M7-02)
 * after a login and read here on join. This build only ever *reads* it: there
 * is no login UI in the client yet, so an ordinary guest simply has nothing
 * stored and joins as one. When a token is present the world verifies it and
 * seats the join on the account's character.
 */
export const ACCESS_TOKEN_KEY = 'annwn.accessToken';

/**
 * Where the refresh token lives. Stored beside the access token from the same
 * handoff (M7-02). This build keeps it but does not yet spend it — active
 * refresh is a later iteration; the access token is checked only at join, and a
 * joined session persists on the socket regardless of its expiry.
 */
export const REFRESH_TOKEN_KEY = 'annwn.refreshToken';

/**
 * Storage as this module needs it, which is three methods of the real thing.
 *
 * Injected rather than reached for so the tests are not a jsdom setup, and so
 * that the no-storage case is a code path rather than a hypothetical — Safari
 * in private mode *throws* from `localStorage.setItem`, and a game that fails
 * to load rather than starting a guest session would be a bug found by a
 * player.
 */
export interface IdentityStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The same shape the server validates against. Kept here rather than imported
 * so the client is not trusting its own stored string on the server's say-so:
 * both ends check, and a mangled entry is replaced rather than sent.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isPlayerId(value: string | null): value is string {
  return value !== null && UUID_PATTERN.test(value);
}

/**
 * The stored player id, or `null` if this browser has never had one.
 *
 * Never throws. A browser that refuses to *read* localStorage is one that will
 * refuse to write it too, and the honest answer in both cases is "this player
 * has no id" — the server mints one, the session works, and it is forgotten
 * when the tab closes. A thrown exception here would instead be a blank page.
 */
export function loadPlayerId(store: IdentityStore | null = defaultStore()): string | null {
  if (!store) return null;
  try {
    const stored = store.getItem(PLAYER_ID_KEY);
    return isPlayerId(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Write down who the server says we are.
 *
 * Called with the `playerId` from the welcome rather than with whatever was
 * sent, so there is one authority on the answer and one code path afterwards:
 * a first-time player, a player whose stored id had been mangled, and a player
 * with a perfectly good one all end up storing what the server confirmed.
 */
export function savePlayerId(id: string, store: IdentityStore | null = defaultStore()): void {
  if (!store || !isPlayerId(id)) return;
  try {
    store.setItem(PLAYER_ID_KEY, id);
  } catch {
    // Full, or private mode. They play this session as a guest and lose the
    // character when the tab closes, which is worse than the alternative and
    // much better than not playing.
  }
}

/**
 * The stored access token, or `null` for a guest. Bounded to the same size the
 * server will accept, and never throws — a browser that will not read storage
 * simply plays as a guest, exactly as it does without a player id.
 */
export function loadAccessToken(store: IdentityStore | null = defaultStore()): string | null {
  if (!store) return null;
  try {
    const stored = store.getItem(ACCESS_TOKEN_KEY);
    return typeof stored === 'string' && stored.length > 0 && stored.length <= 4096 ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Take the tokens the portal handed off in the URL fragment (M7-02) and store
 * them, returning whether any were found so the caller can wipe the fragment.
 *
 * The handoff is cross-origin — the portal at `pendrane.com` owns login, the
 * game runs at `game.pendrane.com`, and the two do not share `localStorage` —
 * so `/play` sends the tokens in `location.hash` (`#access=…&refresh=…`). A
 * fragment, not a query, so the tokens are never put in a request line a server
 * or proxy would log. This reads them once; the caller strips the fragment
 * immediately after, so a shared screenshot or a back-button revisit does not
 * carry a live credential.
 */
export function ingestTokensFromHash(
  hash: string,
  store: IdentityStore | null = defaultStore(),
): boolean {
  if (!store || !hash) return false;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  let found = false;
  for (const [param, key] of [
    ['access', ACCESS_TOKEN_KEY],
    ['refresh', REFRESH_TOKEN_KEY],
  ] as const) {
    const value = params.get(param);
    if (value && value.length > 0 && value.length <= 4096) {
      try {
        store.setItem(key, value);
        found = true;
      } catch {
        // Private mode or a full disk. They fall through to whatever was already
        // stored, or to a guest session — never to a thrown exception.
      }
    }
  }
  return found;
}

/** `localStorage`, or `null` where there is not one (SSR, a worker, a test). */
function defaultStore(): IdentityStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
